// src/components/CredentialInputModal.tsx - Modal for inputting credentials
import React, { useState, useEffect, useCallback } from 'react';
import '../assets/styles/CredentialInputModal.css';

interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'password' | 'textarea';
  required: boolean;
  placeholder?: string;
  helpText?: string;
}

interface ServiceConfig {
  id: string;
  name: string;
  icon: string;
  description: string;
  credentialType: 'oauth' | 'api_key' | 'username_password';
  verb?: string;
  fields: FieldConfig[];
}

interface CredentialInputModalProps {
  service: ServiceConfig;
  onSave: (credentials: Record<string, string>) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}

const CredentialInputModal: React.FC<CredentialInputModalProps> = ({
  service,
  onSave,
  onClose,
  isLoading
}) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Initialize form data
  useEffect(() => {
    const initialData: Record<string, string> = {};
    const initialSecrets: Record<string, boolean> = {};

    service.fields.forEach(field => {
      initialData[field.name] = '';
      if (field.type === 'password') {
        initialSecrets[field.name] = false;
      }
    });

    setFormData(initialData);
    setShowSecrets(initialSecrets);
  }, [service]);

  // Handle input change
  const handleChange = (fieldName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setError(null);
  };

  // Handle field blur
  const handleBlur = (fieldName: string) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Toggle password visibility
  const toggleSecretVisibility = (fieldName: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Validate form
  const validateForm = useCallback((): boolean => {
    for (const field of service.fields) {
      if (field.required && !formData[field.name]?.trim()) {
        return false;
      }
    }
    return true;
  }, [service.fields, formData]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fill in all required fields');
      // Mark all fields as touched
      const allTouched: Record<string, boolean> = {};
      service.fields.forEach(field => {
        allTouched[field.name] = true;
      });
      setTouched(allTouched);
      return;
    }

    try {
      await onSave(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save credentials');
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isLoading, onClose]);

  // Get field error state
  const getFieldError = (field: FieldConfig): string | null => {
    if (!touched[field.name]) return null;
    if (field.required && !formData[field.name]?.trim()) {
      return `${field.label} is required`;
    }
    return null;
  };

  return (
    <div className="credential-modal-backdrop" onClick={handleBackdropClick}>
      <div className="credential-modal">
        <div className="modal-header">
          <div className="modal-title">
            <span className="modal-icon">{service.icon}</span>
            <h2>{service.verb || 'Connect'} {service.name}</h2>
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <p className="modal-description">{service.description}</p>

        {error && (
          <div className="modal-error">
            <span className="error-icon">!</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="credential-form">
          {service.fields.map(field => {
            const fieldError = getFieldError(field);
            const isPassword = field.type === 'password';
            const showValue = isPassword && showSecrets[field.name];

            return (
              <div
                key={field.name}
                className={`form-field ${fieldError ? 'has-error' : ''}`}
              >
                <label htmlFor={field.name}>
                  {field.label}
                  {field.required && <span className="required">*</span>}
                </label>

                <div className="input-wrapper">
                  {field.type === 'textarea' ? (
                    <textarea
                      id={field.name}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      onBlur={() => handleBlur(field.name)}
                      placeholder={field.placeholder}
                      disabled={isLoading}
                      rows={4}
                    />
                  ) : (
                    <input
                      id={field.name}
                      type={isPassword && !showValue ? 'password' : 'text'}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      onBlur={() => handleBlur(field.name)}
                      placeholder={field.placeholder}
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  )}

                  {isPassword && (
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => toggleSecretVisibility(field.name)}
                      tabIndex={-1}
                      aria-label={showValue ? 'Hide' : 'Show'}
                    >
                      {showValue ? '🙈' : '👁️'}
                    </button>
                  )}
                </div>

                {field.helpText && !fieldError && (
                  <p className="help-text">{field.helpText}</p>
                )}

                {fieldError && (
                  <p className="error-text">{fieldError}</p>
                )}
              </div>
            );
          })}

          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={isLoading || !validateForm()}
            >
              {isLoading ? (
                <>
                  <span className="loading-spinner-small" />
                  Saving...
                </>
              ) : (
                'Save Credentials'
              )}
            </button>
          </div>
        </form>

        <div className="modal-security-note">
          <span className="lock-icon">🔒</span>
          <span>Your credentials will be encrypted before storage</span>
        </div>
      </div>
    </div>
  );
};

export default CredentialInputModal;
